/**
 * OnboardingWizard Component
 *
 * Main wizard orchestrator for the onboarding flow.
 * Manages step progression through Welcome → Profile → Partner → Completed.
 * Uses the useOnboarding hook to track and advance state on the backend.
 */

import { useOnboarding } from "../../hooks/useOnboarding";
import { OnboardingProgress } from "./OnboardingProgress";
import { WelcomeStep } from "./steps/WelcomeStep";
import { ProfileStep } from "./steps/ProfileStep";
import { PartnerStep } from "./steps/PartnerStep";
import { CompletionStep } from "./steps/CompletionStep";
import { LoadingSpinner } from "../LoadingSpinner";

const STEP_ORDER = ["welcome", "profile", "partner", "completed"] as const;

export function OnboardingWizard() {
  const { currentStep, steps, advanceStep, isLoading, isError } =
    useOnboarding();

  if (isLoading) {
    return <LoadingSpinner message="Loading onboarding..." />;
  }

  if (isError) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-sm text-destructive">
          Something went wrong loading onboarding. Please refresh the page.
        </p>
      </div>
    );
  }

  // Determine the active step — default to "welcome" for new users (null step)
  const activeStep = currentStep ?? "welcome";

  const handleAdvance = (nextStep: string) => {
    advanceStep.mutate(nextStep);
  };

  const renderStep = () => {
    switch (activeStep) {
      case "welcome":
        return (
          <WelcomeStep
            onContinue={() => handleAdvance("profile")}
            isLoading={advanceStep.isPending}
          />
        );
      case "profile":
        return (
          <ProfileStep
            onContinue={() => handleAdvance("partner")}
            isLoading={advanceStep.isPending}
          />
        );
      case "partner":
        return (
          <PartnerStep
            onContinue={() => handleAdvance("completed")}
            isLoading={advanceStep.isPending}
          />
        );
      case "completed":
        return <CompletionStep />;
      default:
        return (
          <WelcomeStep
            onContinue={() => handleAdvance("profile")}
            isLoading={advanceStep.isPending}
          />
        );
    }
  };

  // Don't show progress indicator on completion step
  const showProgress =
    activeStep !== "completed" && STEP_ORDER.includes(activeStep as never);

  return (
    <div className="space-y-8">
      {showProgress && <OnboardingProgress steps={steps} />}
      {renderStep()}
    </div>
  );
}
